import type {
  AccountSubscription,
  GetAccountUsageQuery,
  PlanFeatureInput,
} from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useAccountPermissions } from '@frameio/permissions';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { getLink } from '@frameio/shared/routing/getLink';
import { formatBytes } from '@frameio/utils/formatters';
import { AlertBanner } from '@frameio/vapor';
import { useIsOnUsagePage } from '../hooks/useIsOnUsagePage';
import { BannerWrapper } from './BannerWrapper';

const SETTINGS_PROJECTS_INACTIVE = getLink({
  page: 'settings',
  section: 'projects',
  filter: { status: 'inactive' },
});
const SETTINGS_USAGE = getLink({ page: 'settings', section: 'usage' });

type StorageText = {
  titleWithPermission: (
    storageCount: string,
    storageLimit: string
  ) => string | React.ReactNode;
  titleNoPermission: string;
  detailsNoPermissionAdobe: string;
  detailsNoPermissionWithGrace: (
    ownerEmail: string,
    graceDate: Date
  ) => string | React.ReactNode;
  detailsNoPermissionNoGrace: (ownerEmail: string) => string | React.ReactNode;
  detailsNoPermissionEnterprise: (
    ownerEmail: string
  ) => string | React.ReactNode;
  detailsWithPermissionEnterprise: (
    manageStorageLink: string
  ) => string | React.ReactNode;
  detailsWithPermissionWithGrace: (
    manageStorageLink: string,
    graceDate: Date
  ) => string | React.ReactNode;
  detailsWithPermissionNoGrace: (
    manageStorageLink: string
  ) => string | React.ReactNode;
};

const getDetails = (
  text: StorageText,
  canManageBilling: boolean,
  graceDate: Date | null,
  isEnterprise: boolean,
  isManagedByAdobe: boolean,
  manageStorageLink: string,
  ownerEmail: string
) => {
  const hasGracePeriod = !!graceDate;

  if (!canManageBilling) {
    if (isManagedByAdobe) {
      return text.detailsNoPermissionAdobe;
    }
    if (isEnterprise) {
      return text.detailsNoPermissionEnterprise(ownerEmail);
    }
    if (hasGracePeriod) {
      return text.detailsNoPermissionWithGrace(ownerEmail, graceDate);
    }
    return text.detailsNoPermissionNoGrace(ownerEmail);
  }

  if (isEnterprise) {
    return text.detailsWithPermissionEnterprise(manageStorageLink);
  }

  if (hasGracePeriod) {
    return text.detailsWithPermissionWithGrace(manageStorageLink, graceDate);
  }

  return text.detailsWithPermissionNoGrace(manageStorageLink);
};

export type StorageAlertBannerBaseProps = {
  account: NonNullable<GetAccountUsageQuery['account']>;
  featureGateKey: PlanFeatureInput;
  getIsStorageAddOnIncluded: (
    addOns?: AccountSubscription['addOns']
  ) => boolean;
  projectsLink?: string;
  storageBytes:
    | { used53?: number | null; limit53?: number | null }
    | null
    | undefined;
  text: StorageText;
};

export function StorageAlertBannerBase({
  account,
  featureGateKey,
  getIsStorageAddOnIncluded,
  projectsLink = SETTINGS_PROJECTS_INACTIVE,
  storageBytes,
  text,
}: StorageAlertBannerBaseProps) {
  const accountId = account?.id;
  const canManageBilling =
    useAccountPermissions({
      accountId,
      check: 'canManageBilling',
    }) ?? false;

  const subscription = account?.subscription;
  const hasStorageAddOn = getIsStorageAddOnIncluded(subscription?.addOns);

  const storageCount = storageBytes?.used53 ?? 0;
  const storageLimit = storageBytes?.limit53;
  const hasUnlimitedStorage = storageLimit === null;
  const isAccountLocked = !!account?.lockedAt;

  const isOnUsagePage = useIsOnUsagePage();

  const showBanner =
    !hasUnlimitedStorage &&
    storageCount > (storageLimit ?? 0) &&
    !isOnUsagePage &&
    !isAccountLocked;

  const tier = subscription?.tier;
  const isEnterprise = tier === 'ENTERPRISE';
  const ownerEmail = account?.owner?.email ?? '';
  const isManagedByAdobe = isAccountAdobeManaged(account?.managedBy);
  const delinquentGracePeriodExpiresAt =
    account?.delinquentGracePeriodExpiresAt ?? '';

  const trial = subscription?.trial;
  const isFree = tier === 'FREE';
  const isTrialActive = trial?.isActive;

  const featureGatePlanSuggestion =
    useFeatureGatePlanSuggestion(featureGateKey);

  const isStorageFeatureEntitled =
    featureGatePlanSuggestion?.includedInCurrentPlan ?? false;

  if (!showBanner) return null;

  const canManageStorage = !isFree && !isTrialActive && canManageBilling;
  const manageStorageLink =
    canManageStorage &&
    !isEnterprise &&
    hasStorageAddOn &&
    isStorageFeatureEntitled &&
    !subscription?.subscriptionEndAt
      ? SETTINGS_USAGE
      : projectsLink;

  const graceDate = delinquentGracePeriodExpiresAt
    ? new Date(delinquentGracePeriodExpiresAt)
    : null;

  const title = canManageBilling
    ? text.titleWithPermission(
        formatBytes(storageCount),
        formatBytes(storageLimit ?? 0)
      )
    : text.titleNoPermission;

  const details = getDetails(
    text,
    canManageBilling,
    graceDate,
    isEnterprise,
    isManagedByAdobe,
    manageStorageLink,
    ownerEmail
  );

  return (
    <BannerWrapper>
      <AlertBanner details={details} title={title} variant="notice" />
    </BannerWrapper>
  );
}
